'use client';

import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Typography } from './typography';
import { TextLink } from './text-link';
import { ReactNode, useRef, useEffect, useState, ElementType } from 'react';

interface CardProps {
  title: string;
  description: string;
  href?: string;
  imageSrc?: string;
  imageAlt?: string;
  icon?: ElementType;
  index?: number;
  showIndex?: boolean;
  showFooterLink?: boolean;
  footerLinkText?: string;
  className?: string;
  onVisible?: () => void;
  animationDelay?: number;
  children?: ReactNode;
  imageHeight?: string;
  imageWidth?: string;
  imagePadding?: string;
  imageLoading?: 'lazy' | 'eager';
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  footerLinkMargin?: string;
  renderContent?: ReactNode;
  imageLinkable?: boolean;
}

export function Card({
  title,
  description,
  href,
  imageSrc,
  imageAlt,
  icon: Icon,
  index,
  showIndex = false,
  showFooterLink = true,
  footerLinkText = 'Explore more',
  className,
  onVisible,
  animationDelay = 0,
  children,
  imageHeight = 'h-32',
  imageWidth = 'w-full',
  imagePadding,
  imageLoading = 'lazy',
  titleVariant = 'h3',
  footerLinkMargin = 'mt-4',
  renderContent,
  imageLinkable = true,
}: CardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cardRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          onVisible?.();
          observer.unobserve(cardRef.current!);
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(cardRef.current);

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [onVisible]);

  return (
    <div
      ref={cardRef}
      style={{ transitionDelay: `${isVisible ? animationDelay : 0}ms` }}
      className={cn(
        'relative flex flex-col justify-between rounded-2xl bg-gray-50 dark:bg-gradient-to-b dark:from-gray-950 dark:via-gray-900 dark:to-black p-6 md:p-7 lg:p-8 shadow-none hover:shadow-none dark:shadow-[0_18px_45px_rgba(0,0,0,0.55)] dark:hover:shadow-[0_24px_65px_rgba(59,130,246,0.45)] transition-all duration-500 group transform',
        isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95',
        className
      )}
    >
      {/* Glowing border ring on hover */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-0 ring-primary-500/0 group-hover:ring-[1.5px] group-hover:ring-primary-500/60 transition-all duration-300" />

      {/* Subtle grid background */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.14),_transparent_55%),_radial-gradient(circle_at_bottom,_rgba(148,163,184,0.24),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.14),_transparent_55%),_radial-gradient(circle_at_bottom,_rgba(15,23,42,0.9),_transparent_55%)] opacity-70" />

      {/* Index badge */}
      {showIndex && index !== undefined && (
        <div className="relative z-10 mb-3 flex items-center justify-between">
          <span className="inline-flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 px-2.5 py-1 text-[10px] font-semibold tracking-[0.16em] text-gray-600 dark:text-gray-400">
            #{String(index + 1).padStart(2, '0')}
          </span>
        </div>
      )}

      <div className="relative z-10 h-full flex flex-col">
        {renderContent ? (
          renderContent
        ) : (
          <>
            {/* Icon */}
            {Icon && (
              <div className="mb-5">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors duration-300">
                  <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
            )}

            {/* Image */}
            {imageSrc && (
              imageLinkable ? (
                <Link
                  href={href || '#'}
                  className={`mb-5 block overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-900/90 ${imagePadding || ''} flex items-center justify-center`}
                  aria-label={imageAlt || title}
                >
                  <Image
                    src={imageSrc}
                    alt={imageAlt || title}
                    width={480}
                    height={270}
                    loading={imageLoading}
                    quality={70}
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 480px"
                    className={`${imageHeight} ${imageWidth} object-cover opacity-95 transition-transform duration-500 group-hover:scale-105 mx-auto`}
                  />
                </Link>
              ) : (
                <div className={`mb-5 block overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-900/90 ${imagePadding || ''} flex items-center justify-center`}>
                  <Image
                    src={imageSrc}
                    alt={imageAlt || title}
                    width={480}
                    height={270}
                    loading={imageLoading}
                    quality={70}
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 480px"
                    className={`${imageHeight} ${imageWidth} object-cover opacity-95 transition-transform duration-500 group-hover:scale-105 mx-auto`}
                  />
                </div>
              )
            )}

            {/* Title */}
            <Typography variant={titleVariant} className="mb-2">
              {title}
            </Typography>

            {/* Description */}
            <Typography variant="p" className="text-sm md:text-base leading-relaxed flex-1">
              {description}
            </Typography>

            {/* Custom children content */}
            {children}

            {/* Footer link */}
            {showFooterLink && href && (
              <div className={`${footerLinkMargin} flex items-center justify-between`}>
                <TextLink href={href} ariaLabel={`Explore more about ${title}`}>
                  {footerLinkText}
                </TextLink>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom divider */}
      <div className="relative z-10 mt-6 h-px w-full bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
    </div>
  );
}

